import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import * as Sentry from '@sentry/react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { useActivateUser } from '@/api/auth/activate-user'
import { API_ERRORS } from '@/constants'
import { getApiErrorStatus } from '@/utils/error'

import AuthLayout from './AuthLayout'

const ActivationSuccess = () => {
  const { t } = useTranslation(['auth'])
  const navigate = useNavigate()
  const { email, hash } = useParams()
  const { mutate: activateUser, isSuccess, isError, error } = useActivateUser()
  // StrictMode runs effects twice in dev — the activation call is rate-limited, so fire it once
  const hasRequestedRef = useRef(false)
  const hasParams = Boolean(email && hash)

  useEffect(() => {
    if (!email || !hash) {
      Sentry.addBreadcrumb({
        category: 'auth',
        level: 'info',
        message: 'ActivationSuccess rendered without email/hash route params',
      })
      return
    }
    if (hasRequestedRef.current) return
    hasRequestedRef.current = true

    activateUser(
      { email, hash },
      {
        onError: () => {
          // An already-used or expired link is an expected state, not a bug — breadcrumb only
          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'info',
            message: 'Account activation failed (likely an already-used or expired link)',
          })
        },
      },
    )
  }, [activateUser, email, hash])

  if (isSuccess) {
    return (
      <AuthLayout>
        <div className="auth__password-reset-success">
          <CheckCircleIcon size={32} />
          <h2>{t('auth:activationSuccess.title')}</h2>
          <button className="button button-primary" onClick={() => navigate('/login')}>
            {t('auth:activationSuccess.continue')}
          </button>
        </div>
      </AuthLayout>
    )
  }

  if (isError || !hasParams) {
    const isRateLimited = getApiErrorStatus(error) === API_ERRORS.TOO_MANY_REQUESTS

    return (
      <AuthLayout>
        <div className="auth__password-reset-success">
          <WarningCircleIcon size={32} />
          <h2>
            {isRateLimited
              ? t('auth:activation.errors.tooManyAttempts')
              : t('auth:activation.errors.invalidLink')}
          </h2>
          {!isRateLimited && <p>{t('auth:activation.errors.invalidLinkDescription')}</p>}
          <button
            className="button button-primary"
            onClick={() => navigate(isRateLimited ? '/login' : '/resend-activation-link')}
          >
            {isRateLimited ? t('auth:shared.backToLogin') : t('auth:emailCheckup.resendActivation')}
          </button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="auth__password-reset-success">
        <h2>{t('auth:activation.verifying')}</h2>
      </div>
    </AuthLayout>
  )
}

export default ActivationSuccess
